/**
 * Document Vault List Page
 *
 * Lists all documents in the company's document vault with:
 * - Document filtering by category and status
 * - Search functionality
 * - Document verification status
 * - Expiry warnings
 * - Quick actions (view, download, delete)
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Folder, Upload, Filter } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Table } from '../../components/common/Table';
import { Input } from '../../components/forms/Input';
import { Select } from '../../components/forms/Select';
import { Alert } from '../../components/common/Alert';
import { EmptyState } from '../../components/common/EmptyState';
import { TableSkeleton } from '../../components/common/Skeleton';
import { mockApi } from '../../services/mockApi';
import { formatFileSize, formatDate } from '../../utils/formatting';
import { PermissionWrapper } from '../../components/auth';
import { UserPermission } from '../../types/user';
import type { Document, DocumentCategory, DocumentStatus } from '../../data/documents';

export const DocumentVaultPage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadDocuments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [documents, searchQuery, categoryFilter, statusFilter]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await mockApi.getCompanyDocuments('org-001'); // Mock company ID
      if (response.success && response.data) {
        setDocuments(response.data);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.name.toLowerCase().includes(query) ||
          doc.fileName.toLowerCase().includes(query) ||
          doc.description?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (categoryFilter !== 'ALL') {
      filtered = filtered.filter((doc) => doc.category === categoryFilter);
    }

    // Status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((doc) => doc.status === statusFilter);
    }

    setFilteredDocuments(filtered);
  };

  const getStatusBadge = (status: DocumentStatus) => {
    const statusMap: Record<DocumentStatus, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      DRAFT: { variant: 'default', label: t('common:status.draft') },
      ACTIVE: { variant: 'success', label: t('common:status.active') },
      ARCHIVED: { variant: 'warning', label: 'Archived' },
      EXPIRED: { variant: 'error', label: t('common:status.expired') },
      PENDING_REVIEW: { variant: 'info', label: 'Pending Review' },
    };
    const config = statusMap[status];
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getCategoryLabel = (category: DocumentCategory): string => {
    const categoryMap: Record<DocumentCategory, string> = {
      LICENSES: 'Licenses',
      POLICIES: 'Policies',
      CERTIFICATIONS: 'Certifications',
      FACILITY: 'Facility',
      TRAINING: 'Training',
      SUPPLIERS: 'Suppliers',
      PRODUCTS: 'Products',
      AUDITS: 'Audits',
      SOPs: 'SOPs',
      OTHER: 'Other',
    };
    return categoryMap[category] || category;
  };

  const isExpiringSoon = (doc: Document): boolean => {
    if (!doc.expiryDate) return false;
    const daysUntilExpiry = Math.ceil(
      (new Date(doc.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const categoryOptions = [
    { value: 'ALL', label: 'All Categories' },
    { value: 'LICENSES', label: 'Licenses' },
    { value: 'POLICIES', label: 'Policies' },
    { value: 'CERTIFICATIONS', label: 'Certifications' },
    { value: 'FACILITY', label: 'Facility' },
    { value: 'TRAINING', label: 'Training' },
    { value: 'SUPPLIERS', label: 'Suppliers' },
    { value: 'PRODUCTS', label: 'Products' },
    { value: 'AUDITS', label: 'Audits' },
    { value: 'SOPs', label: 'SOPs' },
    { value: 'OTHER', label: 'Other' },
  ];

  const statusOptions = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'ARCHIVED', label: 'Archived' },
    { value: 'EXPIRED', label: 'Expired' },
    { value: 'PENDING_REVIEW', label: 'Pending Review' },
  ];

  const expiringCount = documents.filter(isExpiringSoon).length;
  const unverifiedCount = documents.filter((d) => !d.verified).length;

  return (
    <PageLayout
      pageTitle={t('company:documents.title')}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title'), href: '/company/dashboard' },
        { label: t('company:documents.title') },
      ]}
      actions={
        <PermissionWrapper permission={UserPermission.UPLOAD_DOCUMENTS}>
          <Button variant="primary" onClick={() => navigate('/company/documents/upload')}>
            + {t('company:documents.uploadDocument')}
          </Button>
        </PermissionWrapper>
      }
    >
      {/* Alerts for expiring and unverified documents */}
      {expiringCount > 0 && (
        <Alert type="warning" title="Documents Expiring Soon" dismissible className="mb-4">
          {expiringCount} document{expiringCount > 1 ? 's' : ''} will expire within 30 days. Please review and renew as needed.
        </Alert>
      )}

      {unverifiedCount > 0 && (
        <Alert type="info" title="Unverified Documents" dismissible className="mb-4">
          {unverifiedCount} document{unverifiedCount > 1 ? 's are' : ' is'} pending verification by the certification body.
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <Select
              value={categoryFilter}
              options={categoryOptions}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />

            <Select
              value={statusFilter}
              options={statusOptions}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredDocuments.length} of {documents.length} documents
          </div>
        </CardBody>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">{t('common:navigation.documents')}</h3>
        </CardHeader>
        <CardBody>
          {loading ? (
            <TableSkeleton rows={5} />
          ) : filteredDocuments.length === 0 ? (
            documents.length === 0 ? (
              <EmptyState
                icon={<Folder className="w-10 h-10" />}
                title="No Documents in Vault"
                description="Your document vault is empty. Upload important documents like business licenses, certifications, and halal policies to get started."
                action={{
                  label: 'Upload Document',
                  onClick: () => navigate('/company/documents/upload'),
                  icon: <Upload className="w-5 h-5 mr-2" />,
                }}
                secondaryAction={{
                  label: 'View Guidelines',
                  onClick: () => alert('Navigate to document guidelines'),
                }}
              />
            ) : (
              <EmptyState
                icon={<Filter className="w-10 h-10" />}
                title="No Matching Documents"
                description="No documents match your current search or filter criteria. Try adjusting your filters."
                action={{
                  label: 'Clear All Filters',
                  onClick: () => {
                    setSearchQuery('');
                    setCategoryFilter('ALL');
                    setStatusFilter('ALL');
                  },
                }}
              />
            )
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: t('company:documents.documentName'),
                  render: (doc: Document) => (
                    <div>
                      <div className="font-medium text-blue-600 hover:underline cursor-pointer">
                        {doc.name}
                      </div>
                      <div className="text-xs text-gray-500">{doc.fileName}</div>
                      {isExpiringSoon(doc) && (
                        <div className="flex items-center gap-1 mt-1">
                          <AlertTriangle className="w-3 h-3 text-warning-600" />
                          <Badge variant="warning" size="sm">
                            Expiring Soon
                          </Badge>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'category',
                  header: 'Category',
                  render: (doc: Document) => (
                    <Badge variant="default">{getCategoryLabel(doc.category)}</Badge>
                  ),
                },
                {
                  key: 'version',
                  header: 'Version',
                  render: (doc: Document) => (
                    <div className="text-sm">
                      <div className="font-medium">v{doc.currentVersion}</div>
                      <div className="text-xs text-gray-500">{doc.versions.length} version{doc.versions.length > 1 ? 's' : ''}</div>
                    </div>
                  ),
                },
                {
                  key: 'uploadedAt',
                  header: t('company:documents.uploadDate'),
                  render: (doc: Document) => (
                    <div className="text-sm">
                      <div>{formatDate(doc.uploadedAt)}</div>
                      <div className="text-xs text-gray-500">{formatFileSize(doc.fileSize)}</div>
                    </div>
                  ),
                },
                {
                  key: 'expiryDate',
                  header: t('company:documents.expiryDate'),
                  render: (doc: Document) => (
                    <div className="text-sm">
                      {doc.expiryDate ? (
                        <>
                          <div className={isExpiringSoon(doc) ? 'text-orange-600 font-medium' : ''}>
                            {formatDate(doc.expiryDate)}
                          </div>
                          {isExpiringSoon(doc) && (
                            <div className="text-xs text-orange-600">
                              {Math.ceil((new Date(doc.expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days left
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400">No expiry</span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'status',
                  header: t('company:documents.status'),
                  render: (doc: Document) => (
                    <div>
                      {getStatusBadge(doc.status)}
                      {!doc.verified && (
                        <div className="mt-1">
                          <Badge variant="info" size="sm">
                            {t('common:status.unverified')}
                          </Badge>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'actions',
                  header: t('company:documents.actions'),
                  render: (doc: Document) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/company/documents/${doc.id}`)}
                      >
                        {t('common:actions.view')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => alert(`Download document: ${doc.name}`)}
                      >
                        {t('common:actions.download')}
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={filteredDocuments}
              keyExtractor={(doc) => doc.id}
              onRowClick={(doc) => navigate(`/company/documents/${doc.id}`)}
            />
          )}
        </CardBody>
      </Card>
    </PageLayout>
  );
};

export default DocumentVaultPage;

